import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Database, TrendingUp, Search, RefreshCw, ExternalLink, ChevronUp, ChevronDown, Building2, BarChart3 } from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const AdminBrands = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [availableNiches, setAvailableNiches] = useState([]);
  
  // Filters and sorting
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNiche, setSelectedNiche] = useState('all');
  const [sortBy, setSortBy] = useState('mention_count');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const limit = 25;

  useEffect(() => {
    fetchBrands();
  }, [selectedNiche, sortBy, sortOrder, page]);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        sort_by: sortBy,
        sort_order: sortOrder,
        limit: limit.toString(),
        skip: (page * limit).toString()
      });
      
      if (selectedNiche && selectedNiche !== 'all') {
        params.append('niche', selectedNiche);
      }
      
      const response = await axios.get(`${API}/admin/brands?${params}`);
      setBrands(response.data.brands);
      setTotal(response.data.total);
      setAvailableNiches(response.data.available_niches || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching brands:', err);
      setError('Failed to load brands');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(0);
  };

  const filteredBrands = brands.filter(brand =>
    brand.brand_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'bg-green-100 text-green-800';
    if (confidence >= 0.6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getNicheColor = (niche) => {
    const colors = {
      'Technology': 'bg-blue-100 text-blue-800',
      'Gaming': 'bg-purple-100 text-purple-800',
      'Education': 'bg-green-100 text-green-800',
      'Health': 'bg-red-100 text-red-800',
      'Food': 'bg-orange-100 text-orange-800',
      'Finance': 'bg-emerald-100 text-emerald-800',
      'Entertainment': 'bg-pink-100 text-pink-800',
      'Productivity': 'bg-indigo-100 text-indigo-800',
      'Personal Care': 'bg-teal-100 text-teal-800',
      'Creator Tools': 'bg-cyan-100 text-cyan-800',
    };
    return colors[niche] || 'bg-slate-100 text-slate-800';
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return sortOrder === 'desc' ? 
      <ChevronDown className="w-4 h-4 inline ml-1" /> : 
      <ChevronUp className="w-4 h-4 inline ml-1" />;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-slate-500 hover:text-slate-700">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                  <Database className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                    Brand Database
                  </h1>
                  <p className="text-sm text-slate-500">Sponsorship Intelligence</p>
                </div>
              </div>
            </div>
            <Button onClick={fetchBrands} variant="outline" size="sm" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card className="p-4 bg-white" data-testid="total-brands-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>{total}</p>
                <p className="text-sm text-slate-500">Total Brands</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-white" data-testid="total-mentions-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>
                  {brands.reduce((sum, b) => sum + (b.mention_count || 0), 0).toLocaleString()}
                </p>
                <p className="text-sm text-slate-500">Total Mentions</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-white" data-testid="niches-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>{availableNiches.length}</p>
                <p className="text-sm text-slate-500">Niches Covered</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6 bg-white" data-testid="filters-card">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search brands..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            <Select value={selectedNiche} onValueChange={(v) => { setSelectedNiche(v); setPage(0); }}>
              <SelectTrigger className="w-full sm:w-48" data-testid="niche-filter">
                <SelectValue placeholder="All Niches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Niches</SelectItem>
                {availableNiches.map(niche => (
                  <SelectItem key={niche} value={niche}>{niche}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(0); }}>
              <SelectTrigger className="w-full sm:w-48" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mention_count">Mention Count</SelectItem>
                <SelectItem value="detection_confidence">Confidence</SelectItem>
                <SelectItem value="niche_category">Niche</SelectItem>
                <SelectItem value="brand_name">Brand Name</SelectItem>
                <SelectItem value="last_detected_at">Last Detected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Brands Table */}
        {loading ? (
          <Card className="p-12 bg-white text-center">
            <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-4" />
            <p className="text-slate-600">Loading brands...</p>
          </Card>
        ) : error ? (
          <Card className="p-12 bg-white text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={fetchBrands} variant="outline">Try Again</Button>
          </Card>
        ) : (
          <Card className="bg-white overflow-hidden" data-testid="brands-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th 
                      className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('brand_name')}
                    >
                      Brand Name <SortIcon field="brand_name" />
                    </th>
                    <th 
                      className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('mention_count')}
                    >
                      Mentions <SortIcon field="mention_count" />
                    </th>
                    <th 
                      className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('niche_category')}
                    >
                      Niche <SortIcon field="niche_category" />
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Channels
                    </th>
                    <th 
                      className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('detection_confidence')}
                    >
                      Confidence <SortIcon field="detection_confidence" />
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Detection Methods
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredBrands.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                        No brands found matching your criteria
                      </td>
                    </tr>
                  ) : (
                    filteredBrands.map((brand, index) => (
                      <tr 
                        key={brand.detected_brand_id || index} 
                        className="hover:bg-slate-50 transition-colors"
                        data-testid={`brand-row-${index}`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                              <span className="text-indigo-600 font-bold text-sm">
                                {brand.brand_name?.charAt(0)?.toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{brand.brand_name}</p>
                              {brand.detected_urls?.length > 0 && (
                                <a 
                                  href={brand.detected_urls[0]} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-indigo-600 hover:underline flex items-center gap-1"
                                >
                                  {new URL(brand.detected_urls[0]).hostname}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-lg font-semibold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>
                            {brand.mention_count?.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <Badge className={getNicheColor(brand.niche_category)}>
                            {brand.niche_category || 'Unknown'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <span className="font-medium text-slate-900">{brand.detected_channels_count}</span>
                            {brand.sample_channels?.length > 0 && (
                              <p className="text-xs text-slate-500 mt-1 truncate max-w-[200px]">
                                {brand.sample_channels.slice(0, 2).join(', ')}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Badge className={getConfidenceColor(brand.detection_confidence)}>
                            {(brand.detection_confidence * 100).toFixed(0)}%
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {brand.detection_methods?.slice(0, 3).map((method, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {method}
                              </Badge>
                            ))}
                            {(brand.detection_methods?.length || 0) > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{brand.detection_methods.length - 3}
                              </Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {total > limit && (
              <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
                <p className="text-sm text-slate-600">
                  Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total} brands
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={(page + 1) * limit >= total}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}
      </main>
    </div>
  );
};

export default AdminBrands;
